import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

/**
 * Calculates responsive color science values based on LUT preset,
 * intensity (10..100), and slider split ratio (0..100).
 */
const getScopeMultipliers = (lutId, intensityPct, sliderPct) => {
  // Graded portion visible on screen: (100 - sliderPct) / 100
  // When sliderPos = 100%, screen is 100% flat RAW Log-C
  const gradeWeight = Math.max(0, Math.min(1, (100 - sliderPct) / 100)) * (intensityPct / 100);

  // Defaults: Flat Log-C profile (neutral mid-level, low contrast, zero color bias)
  let rGain = 0.5;
  let gGain = 0.5;
  let bGain = 0.5;
  let contrastSpread = 0.22;
  let vecX = 0; // -35 .. +35
  let vecY = 0; // -35 .. +35
  let vecRadius = 10;

  if (lutId === 'teal-orange') {
    // Warm highlights (high Red), cold cyan shadows (high Blue), moderate Green
    rGain = 0.5 + gradeWeight * 0.24;
    gGain = 0.48 + gradeWeight * 0.04;
    bGain = 0.52 + gradeWeight * 0.3;
    contrastSpread = 0.22 + gradeWeight * 0.34;
    vecX = gradeWeight * 22; // towards Orange
    vecY = -gradeWeight * 16; // towards Cyan
    vecRadius = 10 + gradeWeight * 24;
  } else if (lutId === 'kodak-500t') {
    // 3200K Tungsten warm push, gentle green, deep golden tone
    rGain = 0.52 + gradeWeight * 0.28;
    gGain = 0.5 + gradeWeight * 0.12;
    bGain = 0.46 - gradeWeight * 0.08;
    contrastSpread = 0.22 + gradeWeight * 0.28;
    vecX = gradeWeight * 25; // warm amber
    vecY = gradeWeight * 6;
    vecRadius = 10 + gradeWeight * 20;
  } else if (lutId === 'bleach-bypass') {
    // High contrast silver retention, flat desaturated chroma
    rGain = 0.52;
    gGain = 0.5;
    bGain = 0.48;
    contrastSpread = 0.25 + gradeWeight * 0.48;
    vecX = gradeWeight * 3;
    vecY = -gradeWeight * 3;
    vecRadius = Math.max(5, 10 - gradeWeight * 6); // tightly collapsed
  } else if (lutId === 'cyberpunk-neon') {
    // Ultraviolet magenta + electric cyan blooms
    rGain = 0.54 + gradeWeight * 0.3;
    gGain = 0.42 - gradeWeight * 0.14;
    bGain = 0.58 + gradeWeight * 0.36;
    contrastSpread = 0.24 + gradeWeight * 0.38;
    vecX = -gradeWeight * 24; // Magenta
    vecY = -gradeWeight * 22; // Cyan
    vecRadius = 10 + gradeWeight * 28;
  } else if (lutId === 'cinema-noir') {
    // Pure monochrome: identical R=G=B, high tonal dynamic range
    rGain = 0.5;
    gGain = 0.5;
    bGain = 0.5;
    contrastSpread = 0.24 + gradeWeight * 0.44;
    vecX = 0;
    vecY = 0;
    vecRadius = 3; // Zero chroma point
  }

  return { rGain, gGain, bGain, contrastSpread, vecX, vecY, vecRadius, gradeWeight };
};

/**
 * Real-Time Dynamic RGB Parade & Color Vectorscope HUD Component
 */
export const FilmhookColoristScopeHUD = ({
  activeLut,
  sliderPos,
  intensity,
  onClose,
}) => {
  const [scopeMode, setScopeMode] = useState('parade'); // 'parade' | 'vectorscope' | 'waveform'
  const [isExpanded, setIsExpanded] = useState(false);

  const { rGain, gGain, bGain, contrastSpread, vecX, vecY, vecRadius } =
    getScopeMultipliers(activeLut?.id, intensity, sliderPos);

  const hudWidth = isExpanded ? { xs: 290, sm: 340 } : { xs: 230, sm: 260 };
  const hudHeight = isExpanded ? 180 : 138;

  // Generate simulated realistic waveform points
  const generateWavePath = (gain, spread, seed = 0) => {
    const pointsCount = isExpanded ? 24 : 16;
    const height = isExpanded ? 100 : 70;
    const width = isExpanded ? 90 : 66;

    let d = '';
    for (let i = 0; i <= pointsCount; i++) {
      const x = (i / pointsCount) * width;
      // Organic curve with harmonic spikes
      const rawY =
        Math.sin(i * 0.8 + seed) * 0.35 +
        Math.cos(i * 1.4 + seed * 2) * 0.25 +
        Math.sin(i * 2.5) * 0.15;
      const normalizedY = gain + rawY * spread;
      const y = Math.max(4, Math.min(height - 4, (1 - normalizedY) * height));
      d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
    }
    return d;
  };

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        bottom: 14,
        right: 14,
        width: hudWidth,
        bgcolor: 'rgba(2, 6, 23, 0.92)',
        backdropFilter: 'blur(14px)',
        border: '1.5px solid rgba(139, 92, 246, 0.4)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.85), 0 0 16px rgba(139, 92, 246, 0.25)',
        borderRadius: '16px',
        overflow: 'hidden',
        zIndex: 8,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'default',
        userSelect: 'none',
      }}
    >
      {/* ══════════════════════════════════════════════════════════
          1. SCOPE HUD HEADER
          ══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.2,
          py: 0.6,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'rgba(15, 23, 42, 0.6)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <RadarRoundedIcon sx={{ fontSize: 14, color: '#38bdf8' }} />
          <Typography
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              fontSize: '0.62rem',
              fontWeight: 900,
              letterSpacing: '0.08em',
              color: '#38bdf8',
            }}
          >
            COLOR SCOPES
          </Typography>
        </Box>

        {/* Mode Switcher Tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          {[
            { id: 'parade', label: 'RGB' },
            { id: 'vectorscope', label: 'VEC' },
            { id: 'waveform', label: 'LUMA' },
          ].map((mode) => (
            <Box
              key={mode.id}
              onClick={() => setScopeMode(mode.id)}
              sx={{
                px: 0.7,
                py: 0.2,
                borderRadius: '6px',
                fontSize: '0.58rem',
                fontFamily: 'monospace',
                fontWeight: 800,
                cursor: 'pointer',
                bgcolor: scopeMode === mode.id ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                color: scopeMode === mode.id ? '#38bdf8' : '#64748b',
                border:
                  scopeMode === mode.id
                    ? '1px solid rgba(56, 189, 248, 0.4)'
                    : '1px solid transparent',
                '&:hover': {
                  color: '#ffffff',
                },
              }}
            >
              {mode.label}
            </Box>
          ))}

          {/* Expand / Minimize Toggle */}
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: '#ffffff' } }}
          >
            {isExpanded ? (
              <FullscreenExitRoundedIcon sx={{ fontSize: 14 }} />
            ) : (
              <FullscreenRoundedIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>

          {/* Close HUD */}
          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
            >
              <CloseRoundedIcon sx={{ fontSize: 13 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════
          2. SCOPE CANVAS BODY
          ══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          height: hudHeight,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          bgcolor: '#010409',
        }}
      >
        {/* Horizontal IRE Grid Lines */}
        <Box sx={{ position: 'absolute', top: 6, bottom: 6, left: 6, right: 6, pointerEvents: 'none' }}>
          {[0, 25, 50, 75, 100].map((ire) => (
            <Box
              key={ire}
              sx={{
                position: 'absolute',
                top: `${100 - ire}%`,
                left: 0,
                right: 0,
                height: '1px',
                bgcolor: ire === 0 || ire === 100 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.48rem',
                  color: 'rgba(255,255,255,0.25)',
                  position: 'absolute',
                  left: -2,
                  transform: 'translateY(-50%)',
                }}
              >
                {ire}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── MODE 1: RGB PARADE ── */}
        {scopeMode === 'parade' && (
          <Box sx={{ display: 'flex', width: '100%', height: '100%', gap: 0.8, zIndex: 2, pl: 2 }}>
            {/* RED CHANNEL */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ color: '#ef4444', fontSize: '0.55rem', fontWeight: 900, fontFamily: 'monospace' }}>
                R: {Math.round(rGain * 100)}
              </Typography>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
                  <path
                    d={generateWavePath(rGain, contrastSpread, 1)}
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth={isExpanded ? '2' : '1.5'}
                    style={{
                      filter: 'drop-shadow(0 0 6px #ef4444)',
                      transition: 'd 0.15s ease',
                    }}
                  />
                  {/* Density glow shadow fill */}
                  <path
                    d={`${generateWavePath(rGain, contrastSpread, 1)} L ${isExpanded ? 90 : 66} ${isExpanded ? 100 : 70} L 0 ${isExpanded ? 100 : 70} Z`}
                    fill="rgba(239, 68, 68, 0.12)"
                  />
                </svg>
              </Box>
            </Box>

            {/* GREEN CHANNEL */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ color: '#22c55e', fontSize: '0.55rem', fontWeight: 900, fontFamily: 'monospace' }}>
                G: {Math.round(gGain * 100)}
              </Typography>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
                  <path
                    d={generateWavePath(gGain, contrastSpread, 2)}
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth={isExpanded ? '2' : '1.5'}
                    style={{
                      filter: 'drop-shadow(0 0 6px #22c55e)',
                      transition: 'd 0.15s ease',
                    }}
                  />
                  <path
                    d={`${generateWavePath(gGain, contrastSpread, 2)} L ${isExpanded ? 90 : 66} ${isExpanded ? 100 : 70} L 0 ${isExpanded ? 100 : 70} Z`}
                    fill="rgba(34, 197, 94, 0.12)"
                  />
                </svg>
              </Box>
            </Box>

            {/* BLUE CHANNEL */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ color: '#38bdf8', fontSize: '0.55rem', fontWeight: 900, fontFamily: 'monospace' }}>
                B: {Math.round(bGain * 100)}
              </Typography>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
                  <path
                    d={generateWavePath(bGain, contrastSpread, 3)}
                    fill="none"
                    stroke="#38bdf8"
                    strokeWidth={isExpanded ? '2' : '1.5'}
                    style={{
                      filter: 'drop-shadow(0 0 6px #38bdf8)',
                      transition: 'd 0.15s ease',
                    }}
                  />
                  <path
                    d={`${generateWavePath(bGain, contrastSpread, 3)} L ${isExpanded ? 90 : 66} ${isExpanded ? 100 : 70} L 0 ${isExpanded ? 100 : 70} Z`}
                    fill="rgba(56, 189, 248, 0.12)"
                  />
                </svg>
              </Box>
            </Box>
          </Box>
        )}

        {/* ── MODE 2: VECTORSCOPE ── */}
        {scopeMode === 'vectorscope' && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <svg
              width={isExpanded ? 150 : 110}
              height={isExpanded ? 150 : 110}
              viewBox="-60 -60 120 120"
              style={{ overflow: 'visible' }}
            >
              {/* Radar Reticle Rings */}
              <circle cx="0" cy="0" r="50" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
              <circle cx="0" cy="0" r="35" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" strokeDasharray="2,2" />
              <circle cx="0" cy="0" r="20" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
              <line x1="-50" y1="0" x2="50" y2="0" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              <line x1="0" y1="-50" x2="0" y2="50" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

              {/* Skin Tone I-Line reference (Flesh Tone Axis ~57°) */}
              <line
                x1="0"
                y1="0"
                x2="35"
                y2="-26"
                stroke="rgba(245, 158, 11, 0.6)"
                strokeWidth="1.5"
                strokeDasharray="3,2"
              />
              <text x="36" y="-28" fill="#f59e0b" fontSize="6" fontFamily="monospace" fontWeight="900">
                SKIN
              </text>

              {/* Six Primary Color Targets: Red, Magenta, Blue, Cyan, Green, Yellow */}
              <rect x="24" y="-36" width="6" height="6" fill="none" stroke="#ef4444" strokeWidth="1" />
              <text x="32" y="-32" fill="#ef4444" fontSize="6" fontFamily="monospace">R</text>

              <rect x="-30" y="-30" width="6" height="6" fill="none" stroke="#ec4899" strokeWidth="1" />
              <text x="-42" y="-26" fill="#ec4899" fontSize="6" fontFamily="monospace">Mg</text>

              <rect x="-40" y="10" width="6" height="6" fill="none" stroke="#3b82f6" strokeWidth="1" />
              <text x="-50" y="15" fill="#3b82f6" fontSize="6" fontFamily="monospace">B</text>

              <rect x="-26" y="32" width="6" height="6" fill="none" stroke="#06b6d4" strokeWidth="1" />
              <text x="-24" y="44" fill="#06b6d4" fontSize="6" fontFamily="monospace">Cy</text>

              <rect x="22" y="26" width="6" height="6" fill="none" stroke="#22c55e" strokeWidth="1" />
              <text x="30" y="32" fill="#22c55e" fontSize="6" fontFamily="monospace">G</text>

              <rect x="36" y="-6" width="6" height="6" fill="none" stroke="#eab308" strokeWidth="1" />
              <text x="44" y="-2" fill="#eab308" fontSize="6" fontFamily="monospace">Yl</text>

              {/* Dynamic Glowing Chromatic Cloud (Centroid vector of active grade) */}
              <circle
                cx={vecX}
                cy={vecY}
                r={vecRadius}
                fill={activeLut?.color || '#06b6d4'}
                opacity="0.32"
                style={{
                  filter: `drop-shadow(0 0 10px ${activeLut?.color || '#06b6d4'})`,
                  transition: 'all 0.2s ease',
                }}
              />
              <circle
                cx={vecX}
                cy={vecY}
                r="3"
                fill="#ffffff"
                style={{
                  filter: 'drop-shadow(0 0 6px #fff)',
                  transition: 'all 0.2s ease',
                }}
              />
            </svg>
          </Box>
        )}

        {/* ── MODE 3: LUMA WAVEFORM ── */}
        {scopeMode === 'waveform' && (
          <Box sx={{ width: '100%', height: '100%', position: 'relative', zIndex: 2, pl: 2 }}>
            <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
              {/* Highlight clipping limit warning */}
              <line x1="0" y1="6" x2="100%" y2="6" stroke="#ef4444" strokeWidth="1" strokeDasharray="3,3" />
              {/* Shadow crush limit */}
              <line
                x1="0"
                y1={isExpanded ? 100 : 70}
                x2="100%"
                y2={isExpanded ? 100 : 70}
                stroke="#38bdf8"
                strokeWidth="1"
                strokeDasharray="3,3"
              />

              {/* Combined Luminance Traces */}
              <path
                d={generateWavePath((rGain + gGain + bGain) / 3, contrastSpread, 4)}
                fill="none"
                stroke="#ffffff"
                strokeWidth={isExpanded ? '2' : '1.5'}
                style={{
                  filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.8))',
                  transition: 'd 0.15s ease',
                }}
              />
              <path
                d={`${generateWavePath((rGain + gGain + bGain) / 3, contrastSpread, 4)} L ${isExpanded ? 260 : 190} ${isExpanded ? 100 : 70} L 0 ${isExpanded ? 100 : 70} Z`}
                fill="rgba(255, 255, 255, 0.08)"
              />
            </svg>
          </Box>
        )}
      </Box>

      {/* ══════════════════════════════════════════════════════════
          3. HUD STATUS FOOTER
          ══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.2,
          py: 0.4,
          background: 'rgba(15, 23, 42, 0.7)',
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <Typography sx={{ color: '#22c55e', fontSize: '0.52rem', fontWeight: 800, fontFamily: 'monospace' }}>
          ● LIVE 10-BIT
        </Typography>

        <Typography sx={{ color: activeLut?.color || '#38bdf8', fontSize: '0.52rem', fontWeight: 800, fontFamily: 'monospace' }}>
          {activeLut?.name} ({intensity}%)
        </Typography>
      </Box>
    </Box>
  );
};

export default FilmhookColoristScopeHUD;
